import { useCallback, useMemo, useState } from "react";

import { DocumentNode } from "@apollo/client";
import { Icon } from "@blueprintjs/core";

import { useAuth } from "@/components/lib/auth";
import { AddPermissionsView } from "@/components/pieces/roleplays/sharing/AddPermissionsView";
import { Button } from "@/components/ui/button";
import { ShareDialog, ShareSelector } from "@/components/ui/sharing";
import { PermissionLevel, ResourceType, VisibilityScope } from "@/graphql";
import {
  ResourcePermissionUser,
  SharePermissionState,
} from "@/types/permissions";

import { ManagePermissionsView } from "./ManagePermissionsView";
import { SimplifiedShareView } from "./SimplifiedShareView";
import { useResourceMutations } from "./useResourceMutations";
import { useResourcePermissions } from "./useResourcePermissions";
import { useShareModalState } from "./useShareModalState";

interface ResourceShareModalProps<T> {
  resource: T;
  resourceType: ResourceType;
  resourceName: string;
  isOpen: boolean;
  onClose: () => void;
  loading: boolean;
  refetchQuery?: DocumentNode;
  refetchVariables?: Record<string, string>;
  workspaceInfoMessage?: string;
  canUpdatePermission?: boolean | null;
  owner?: {
    id: string;
    name?: string | null;
    email?: string | null;
    photo?: string | null;
  } | null;
}

export function ResourceShareModal<
  T extends {
    id: string;
    visibilityScope?: VisibilityScope | null;
    canShareToWorkspace?: boolean | null;
    maxGrantablePermissionLevel?: PermissionLevel | null;
  },
>({
  resource,
  resourceType,
  resourceName,
  isOpen,
  onClose,
  loading: externalLoading,
  refetchQuery,
  refetchVariables,
  workspaceInfoMessage,
  canUpdatePermission,
  owner,
}: ResourceShareModalProps<T>) {
  const { currentWorkspace } = useAuth();
  const [selectorKey, setSelectorKey] = useState(0);

  // Determine if we should show simplified view
  const isGlobal = resource.visibilityScope === VisibilityScope.Global;
  const hasEditPermission = canUpdatePermission !== false;
  const showSimplifiedView = isGlobal || !hasEditPermission;

  // State management
  const {
    state,
    dispatch,
    setSelectedUsers,
    setSelectedGroups,
    setSelectedPermission,
    setNotification,
    setAutoShareSessions,
    resetAll,
  } = useShareModalState({ resource });

  // Close handler with cleanup
  const handleClose = useCallback(() => {
    onClose();
    resetAll();
    setSelectorKey((prev) => prev + 1);
  }, [onClose, resetAll]);

  // Permission data management - only load for normal view
  const { loading: permissionsLoading, refetchPermissions } =
    useResourcePermissions({
      resource,
      resourceType,
      isOpen: isOpen && !showSimplifiedView,
      onPermissionsLoaded: (data) => {
        dispatch({ type: "SET_PERMISSIONS_DATA", payload: data });
      },
    });

  // Mutation handlers
  const { createLoading, updateLoading, createPermissions, updatePermissions } =
    useResourceMutations({
      resource,
      resourceType,
      refetchQuery,
      refetchVariables,
      onSuccess: handleClose,
      refetchPermissions,
    });

  // Update share status handler
  const handleUpdateShareStatus = useCallback(
    (index: number, status: SharePermissionState) => {
      const items = [
        ...state.sharedUsers,
        ...state.sharedGroups,
        ...state.sharedPrograms.slice(0, state.showAllPrograms ? undefined : 5),
      ];

      const item = items[index];
      if (!item) return;

      // Determine if it's a user or group based on properties
      if ("email" in item) {
        dispatch({
          type: "UPDATE_USER_PERMISSION",
          payload: { userId: item.id, status },
        });
      } else if ("workspace" in item) {
        dispatch({
          type: "UPDATE_GROUP_PERMISSION",
          payload: { groupId: item.id, status },
        });
      }
    },
    [
      state.sharedUsers,
      state.sharedGroups,
      state.sharedPrograms,
      state.showAllPrograms,
      dispatch,
    ],
  );

  // Visibility scope change handler
  const handleVisibilityScopeChange = useCallback(
    (scope: "User" | "Workspace") => {
      dispatch({
        type: "SET_VISIBILITY_SCOPE",
        payload:
          scope === "User" ? VisibilityScope.User : VisibilityScope.Workspace,
      });
    },
    [dispatch],
  );

  // Mode checks
  const isAddMode =
    state.selectedUsers.length > 0 || state.selectedGroups.length > 0;
  const loading =
    externalLoading || permissionsLoading || createLoading || updateLoading;

  // Handle back button click
  const handleBack = useCallback(() => {
    // Clear the state and reset the ShareSelector
    setSelectedUsers([]);
    setSelectedGroups([]);
    setSelectorKey((prev) => prev + 1);
  }, [setSelectedUsers, setSelectedGroups]);

  const displayName = resourceName === "Roleplay" ? "Collection" : resourceName;

  // Determine available permissions based on maxGrantablePermissionLevel
  const availablePermissions = useMemo<SharePermissionState[]>(() => {
    const maxLevel = resource.maxGrantablePermissionLevel;

    if (!maxLevel) {
      // If no max level is set, user cannot share at all
      return [];
    }

    switch (maxLevel) {
      case PermissionLevel.View:
        // Can only grant VIEW
        return ["VIEW"];
      case PermissionLevel.Share:
        // Can grant VIEW only (user with SHARE can only grant VIEW)
        return ["VIEW"];
      case PermissionLevel.Monitor:
        // Can grant VIEW and SHARE (user with MONITOR can grant up to SHARE)
        return ["VIEW", "SHARE"];
      case PermissionLevel.Edit:
        // Can grant VIEW, SHARE, MONITOR, or EDIT
        return ["VIEW", "SHARE", "MONITOR", "EDIT"];
      default:
        return [];
    }
  }, [resource.maxGrantablePermissionLevel]);

  // Dialog title with back button when in add mode
  const dialogTitle = isAddMode ? (
    <div className="flex items-center gap-1 -mx-2">
      <Button
        variant="minimal"
        intent="secondary"
        size="icon"
        onClick={handleBack}
      >
        <Icon icon="arrow-left" size={16} style={{ margin: 0 }} />
      </Button>
      <div className="text-lg">Share {displayName}</div>
    </div>
  ) : (
    `Share ${displayName}`
  );

  return (
    <ShareDialog open={isOpen} onOpenChange={handleClose} title={dialogTitle}>
      {showSimplifiedView ? (
        <SimplifiedShareView
          visibilityScope={resource.visibilityScope!}
          owner={
            owner && owner.email
              ? ({
                  ...owner,
                  email: owner.email,
                  shareStatus: "SHARE" as const,
                  originalPermissionLevel: "SHARE",
                  permissionId: "",
                  isOwner: true,
                } as ResourcePermissionUser)
              : undefined
          }
          onClose={handleClose}
          workspaceInfoMessage={workspaceInfoMessage}
        />
      ) : (
        <>
          {!currentWorkspace.isPersonal && (
            <ShareSelector
              key={selectorKey}
              placeholder="Add people and groups"
              onChange={(users, groups) => {
                setSelectedUsers(users);
                setSelectedGroups(groups);
              }}
              showPermissionSelector={isAddMode}
              selectedPermission={state.selectedPermission}
              onPermissionChange={setSelectedPermission}
              availablePermissions={availablePermissions}
              className="mb-5"
            />
          )}

          {isAddMode ? (
            <AddPermissionsView
              selectedUsers={state.selectedUsers}
              selectedGroups={state.selectedGroups}
              isNotified={state.isNotified}
              message={state.message}
              onNotificationChange={(isNotified) =>
                setNotification(isNotified, state.message)
              }
              onMessageChange={(message) =>
                setNotification(state.isNotified, message)
              }
              onShare={() => createPermissions(state)}
              onCancel={handleBack}
              loading={loading}
            />
          ) : (
            <ManagePermissionsView
              resourceType={resourceType}
              sharedUsers={state.sharedUsers}
              sharedGroups={state.sharedGroups}
              sharedPrograms={state.sharedPrograms}
              visibilityScope={state.visibilityScope}
              autoShareSessions={state.autoShareSessions}
              showAllPrograms={state.showAllPrograms}
              canShareToWorkspace={resource?.canShareToWorkspace || false}
              loading={permissionsLoading}
              availablePermissions={availablePermissions}
              onUpdateShareStatus={handleUpdateShareStatus}
              onToggleShowPrograms={() =>
                dispatch({
                  type: "SET_SHOW_ALL_PROGRAMS",
                  payload: !state.showAllPrograms,
                })
              }
              onVisibilityScopeChange={handleVisibilityScopeChange}
              onAutoShareChange={setAutoShareSessions}
              onSave={() => {
                if (!state.hasChanges) {
                  handleClose();
                } else {
                  updatePermissions(state);
                }
              }}
              saveDisabled={!state.hasChanges}
              saveLoading={loading}
              workspaceInfoMessage={workspaceInfoMessage}
            />
          )}
        </>
      )}
    </ShareDialog>
  );
}
